import React from 'react';
import { useGetAllCategories } from 'hooks/ProductCategoryHooks';
import { useDefaultShopDataAction } from 'store';
import { mappingTreeData } from 'utils';
import { useGetProductList } from 'hooks/ProductHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetProductPolicies } from 'hooks/ProductPolicyHooks';
import { useGetPromotions } from 'hooks/PromotionHooks';
import { useGetInquiries } from 'hooks/InquiryHooks';
import { useGetNotices } from 'hooks/NoticeHooks';
import { useGetEvents } from 'hooks/EventHooks';
import { useGetSetting } from 'hooks/SettingHooks';
import dayjs from 'dayjs';
import { useGetMapGroupProducts } from 'hooks/GroupProductHooks';

const DefaultDataProvider = ({ children }) => {
  /* ===== Variables ===== */
  const TODAY = dayjs();
  const options = {
    gcTime: Infinity,
    // staleTime: Infinity,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];
      else return data;
    },
    initialData: [],
  };

  /* ===== Store ===== */
  const action = useDefaultShopDataAction();

  /* ===== Data ===== */
  /**
   * SECTION 카테고리 데이터
   * --
   */
  const { data: ProductCategories } = useGetAllCategories(
    {
      is_show: 'Y',
      type: 'PRODUCT',
      application_status: 'APPROVE',
    },
    options
  );

  React.useEffect(() => {
    if (!ProductCategories.length) return;

    const treeData = mappingTreeData(
      ProductCategories,
      'product_category_id',
      'parents_id',
      'order'
    );

    action.setData('categories', treeData);
    // eslint-disable-next-line
  }, [ProductCategories]);

  /**
   * SECTION 상품 데이터
   * --
   */
  const { data: Products } = useGetProductList(
    {
      is_show: 'Y',
      application_status: 'APPROVE',
    },
    options
  );

  React.useEffect(() => {
    if (!Products.length) return;

    // 상품 상태가 SOLD_OUT, NORMAL인 상태만 표시
    // 미노출 태그 필터 처리
    const newProducts = Products.filter(
      (product) =>
        product.product_status === 'SOLDOUT' ||
        product.product_status === 'NORMAL'
    ).map((product) => ({
      ...product,
      tag: product.tag.filter((tag) => tag.is_show === 'Y'),
    }));

    action.setData('products', newProducts);
    // eslint-disable-next-line
  }, [Products]);

  /**
   * SECTION 그룹 상품 데이터
   * --
   */
  const { data: Group } = useGetMapGroupProducts(
    {
      is_show: 'Y',
    },
    options
  );

  React.useEffect(() => {
    if (!Group.length) return;

    const newGroups = Group.map((group) => ({
      ...group,
      map_product_policy: group.map_product_policy.filter(
        (item) => item.is_enable === 'Y'
      ),
    }));

    action.setData('groups', newGroups);
    // eslint-disable-next-line
  }, [Group]);

  /**
   * SECTION 입점업체 데이터
   * --
   */
  const { data: ShopList } = useGetShopInformations(
    {
      application_status: 'APPROVE',
      join_tables: '["shop_information_file"]',
    },
    options
  );

  React.useEffect(() => {
    if (!ShopList.length) return;

    const newShopList = ShopList.filter((shop) => shop.type !== 'STORE');

    action.setData('shops', newShopList);
    // eslint-disable-next-line
  }, [ShopList]);

  /**
   * SECTION 상품정책 데이터
   * --
   */
  const { data: Polices } = useGetProductPolicies(
    {
      application_status: 'APPROVE',
      join_tables: '["map_product_policy"]',
    },
    options
  );

  React.useEffect(() => {
    if (!Polices.length) return;

    const newPolicyList = Polices.filter(
      (policy) => policy.policy_type !== 'MENU'
    ).map((policy) => ({
      ...policy,
      map_product_policy: policy.map_product_policy.filter(
        (map) => map.is_enable === 'Y'
      ),
    }));

    action.setData('policies', newPolicyList);
    // eslint-disable-next-line
  }, [Polices]);

  /**
   * SECTION 프로모션 데이터
   * --
   */
  const { data: Promotions } = useGetPromotions(
    {
      examination_status: 'APPROVE',
      is_show: 'Y',
      promotion_start_date: `${TODAY.startOf('day').unix()}`,
      promotion_end_date: `${TODAY.endOf('day').unix()}`,
    },
    options
  );

  React.useEffect(() => {
    if (!Promotions.length) return;

    action.setData('promotion', Promotions);
    // eslint-disable-next-line
  }, [Promotions]);

  /**
   * SECTION FAQ 데이터
   * --
   */
  const { data: FAQ } = useGetInquiries(
    {
      inquiry_type: 'FAQ',
    },
    options
  );

  React.useEffect(() => {
    if (!FAQ.length) return;

    action.setData('faqList', FAQ);
    // eslint-disable-next-line
  }, [FAQ]);

  /**
   * SECTION 공지사항 데이터
   * --
   */
  const { data: Notices } = useGetNotices(
    {
      is_show: 'Y',
    },
    options
  );

  React.useEffect(() => {
    if (!Notices.length) return;

    const newNotices = Notices.filter(
      (notice) => notice.show_target === 'ALL' || notice.show_target === 'USER'
    );

    action.setData('notices', newNotices);
    // eslint-disable-next-line
  }, [Notices]);

  /**
   * SECTION 이벤트 데이터
   * --
   */
  const { data: Events } = useGetEvents(
    {
      is_show: 'Y',
    },
    options
  );

  React.useEffect(() => {
    if (!Events.length) return;

    action.setData('events', Events);
    // eslint-disable-next-line
  }, [Events]);

  /**
   * SECTION 관리자 설정
   * --
   */
  const { data: Setting } = useGetSetting(1, null, {
    gcTime: 'Infinity',
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;
      else return data;
    },
    initialData: null,
  });

  React.useEffect(() => {
    if (!Setting) return;

    action.setData('settings', Setting);
    // eslint-disable-next-line
  }, [Setting]);

  /* ===== Render ===== */
  return <React.Fragment>{children}</React.Fragment>;
};

export default DefaultDataProvider;
